import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface LandingStSettings {
  id: string;
  // Product sync
  webhook_token: string;
  endpoint_url: string;
  enabled: boolean;
  // Booking sync
  booking_webhook_token: string | null;
  booking_endpoint_url: string | null;
  booking_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export function useLandingStSettings() {
  const [settings, setSettings] = useState<LandingStSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('vietnamtravel_landing_st_settings')
        .select('*')
        .maybeSingle();

      if (err) throw err;
      setSettings(data || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load landing.st settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (updates: Partial<LandingStSettings>) => {
    if (!settings?.id) {
      const { data, error: err } = await supabase
        .from('vietnamtravel_landing_st_settings')
        .insert({
          ...updates,
          enabled: updates.enabled ?? true,
          booking_enabled: updates.booking_enabled ?? false,
        })
        .select()
        .single();
      if (err) return { error: err.message };
      setSettings(data);
      return { error: null };
    }
    const { data, error: err } = await supabase
      .from('vietnamtravel_landing_st_settings')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', settings.id)
      .select()
      .single();
    if (err) return { error: err.message };
    setSettings(data);
    return { error: null };
  };

  return { settings, loading, error, updateSettings, refetch: fetchSettings };
}